// API service for communicating with the backend
import { ChartData } from './types';
import { API_BASE } from './env';
import { apiFetch } from './auth/apiClient';

export interface AnalysisRequest {
  id: string;
  symbol: string;
  strategy: string;
  parameters: Record<string, any>;
  status: string;
  createdAt: string;
  completedAt?: string;
  result?: any;
}

export interface Asset {
  symbol: string;
  exchange: string;
  name: string;
  status: string;
  class: string;
  tradable: boolean;
  marginable: boolean;
  shortable: boolean;
  easyToBorrow: boolean;
  fractionable: boolean;
}

export interface SubscriptionStats {
  dataFeed: string;
  activeEquitySubscriptions: number;
  maxEquitySubscriptions: number;
  totalTrackedSymbols: number;
}

export interface BulkSubscribeResult {
  message: string;
  succeeded: number;
  failed: number;
  limitReached: boolean;
  stats?: SubscriptionStats;
}

export interface BarData {
  symbol: string;
  openBidPrice: number;
  openAskPrice: number;
  closeBidPrice: number;
  closeAskPrice: number;
  highBidPrice: number;
  highAskPrice: number;
  lowBidPrice: number;
  lowAskPrice: number;
  volume: number;
  vwap?: number;
  tradeCount?: number;
  barStartTime: string;
  barEndTime: string;
  opened: boolean;
  closed: boolean;
}

export interface MarketIndexSnapshot {
  symbol: string;
  price: number;
  prevClose: number;
  changePercent: number;
}

export interface NewsItem {
  id: string;
  title: string;
  summary: string;
  source: string;
  publishedAt: string;
  symbols?: string[];
  sentiment?: 'positive' | 'negative' | 'neutral';
}

class ApiService {
  // Updated getChartData method to support both chartId and symbol/timeframe patterns
  async getChartData(symbol: string, timeframe: string = '1M', barCount: number = 50): Promise<{ success: boolean; data: ChartData; error?: string }> {
    try {
      // Create chartId from symbol and timeframe (e.g., "AAPL_1M")
      const chartId = `${symbol}_${timeframe}`;
      const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}?barCount=${barCount}`);
      if (!response.ok) {
        return {
          success: false,
          data: null as any,
          error: `Failed to fetch chart data: ${response.statusText}`
        };
      }
      const chartData = await response.json();
      return {
        success: true,
        data: chartData
      };
    } catch (error) {
      return {
        success: false,
        data: null as any,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Legacy method for backward compatibility
  async getChartDataById(chartId: string, barCount: number = 50): Promise<ChartData> {
    const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}?barCount=${barCount}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch chart data: ${response.statusText}`);
    }
    return response.json();
  }

  // Enhanced Chart API methods
  async createChart(symbol: string, timeframe: string, chartId: string): Promise<any> {
    const request = {
      symbol,
      exchange: 'NASDAQ',
      periodEnum: 'Day',
      timeUnits: 1,
      chartId,
    };
    const response = await apiFetch(`${API_BASE}/api/Chart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(`Failed to create chart: ${response.statusText}`);
    }
    return response.json();
  }

  async getChart(chartId: string, barCount?: number): Promise<any> {
    let url = `${API_BASE}/api/Chart/${chartId}`;
    if (barCount) {
      url += `?barCount=${barCount}`;
    }
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get chart: ${response.statusText}`);
    }
    return response.json();
  }

  async getAllCharts(): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Chart`);
    if (!response.ok) {
      throw new Error(`Failed to get all charts: ${response.statusText}`);
    }
    return response.json();
  }

  async getChartsForSymbol(symbol: string, exchange?: string): Promise<any> {
    let url = `${API_BASE}/api/Chart/symbol/${symbol}`;
    if (exchange) {
      url += `?exchange=${exchange}`;
    }
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get charts for symbol: ${response.statusText}`);
    }
    return response.json();
  }

  async addIndicatorToChart(chartId: string, indicatorRequest: any): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}/indicators`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(indicatorRequest),
    });
    if (!response.ok) {
      throw new Error(`Failed to add indicator to chart: ${response.statusText}`);
    }
    return response.json();
  }

  async removeIndicatorFromChart(chartId: string, indicatorName: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}/indicators/${indicatorName}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to remove indicator from chart: ${response.statusText}`);
    }
    return response.json();
  }

  async updateIndicatorParameters(chartId: string, indicatorName: string, parameters: any): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}/indicators/${indicatorName}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(parameters),
    });
    if (!response.ok) {
      throw new Error(`Failed to update indicator parameters: ${response.statusText}`);
    }
    return response.json();
  }

  async deleteChart(chartId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to delete chart: ${response.statusText}`);
    }
  }

  async getAvailableIndicators(): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Chart/indicators`);
    if (!response.ok) {
      throw new Error(`Failed to get available indicators: ${response.statusText}`);
    }
    return response.json();
  }

  async refreshChart(chartId: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Chart/${chartId}/refresh`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to refresh chart: ${response.statusText}`);
    }
    return response.json();
  }

  async subscribeToSymbol(symbol: string, timeframe?: string): Promise<any> {
    const url = timeframe 
      ? `${API_BASE}/Screener/subscribe?symbol=${symbol}&timeframe=${timeframe}`
      : `${API_BASE}/Screener/subscribe?symbol=${symbol}`;
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to subscribe to ${symbol}: ${response.statusText}`);
    }
    return response.json();
  }

  async unsubscribeFromSymbol(symbol: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/Screener/unsubscribe?symbol=${symbol}`);
    if (!response.ok) {
      throw new Error(`Failed to unsubscribe from ${symbol}: ${response.statusText}`);
    }
    return response.json();
  }

  async getStoredAssets(): Promise<Asset[]> {
    const response = await apiFetch(`${API_BASE}/Screener/storedAssets`);
    if (!response.ok) {
      throw new Error(`Failed to get stored assets: ${response.statusText}`);
    }
    return response.json();
  }

  // Screener API methods
  async getSubscribedSymbols(page?: number, pageSize?: number): Promise<any> {
    let url = `${API_BASE}/Screener/SubscribedSymbols`;
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (pageSize) params.set('pageSize', String(pageSize));
    if (params.toString()) url += `?${params}`;
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get subscribed symbols: ${response.statusText}`);
    }
    return response.json();
  }

  async getSubscribedSymbolNames(): Promise<{ symbols: string[]; count: number }> {
    const response = await apiFetch(`${API_BASE}/Screener/SubscribedSymbolNames`);
    if (!response.ok) {
      throw new Error(`Failed to get subscribed symbol names: ${response.statusText}`);
    }
    return response.json();
  }

  async getBars(symbol: string, period: string, timeUnits: number): Promise<BarData[]> {
    const response = await apiFetch(`${API_BASE}/Screener/bars?symbol=${symbol}&period=${period}&timeUnits=${timeUnits}`);
    if (!response.ok) {
      throw new Error(`Failed to get bars: ${response.statusText}`);
    }
    return response.json();
  }

  async analyzeSymbol(symbol: string, period: string, timeUnits: number): Promise<any> {
    const response = await apiFetch(`${API_BASE}/Screener/analyze?symbol=${symbol}&period=${period}&timeUnits=${timeUnits}`);
    if (!response.ok) {
      throw new Error(`Failed to analyze symbol: ${response.statusText}`);
    }
    return response.json();
  }

  async addBarType(symbol: string, periodEnum: string, timeUnits: number): Promise<any> {
    const response = await apiFetch(`${API_BASE}/Screener/addBarType?symbol=${symbol}&periodEnum=${periodEnum}&timeUnits=${timeUnits}`);
    if (!response.ok) {
      throw new Error(`Failed to add bar type: ${response.statusText}`);
    }
    return response.json();
  }

  async getAssets(): Promise<Asset[]> {
    const response = await apiFetch(`${API_BASE}/Screener/getAssets`);
    if (!response.ok) {
      throw new Error(`Failed to get assets: ${response.statusText}`);
    }
    return response.json();
  }

  async fetchAssets(): Promise<any> {
    const response = await apiFetch(`${API_BASE}/Screener/fetchAssets`);
    if (!response.ok) {
      throw new Error(`Failed to fetch assets: ${response.statusText}`);
    }
    return response.json();
  }

  async fetchTickData(symbol: string, date: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/Screener/fetchTickData?symbol=${symbol}&date=${date}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch tick data: ${response.statusText}`);
    }
    return response.json();
  }

  // Analysis API methods
  async submitAnalysis(request: any): Promise<AnalysisRequest> {
    const response = await apiFetch(`${API_BASE}/api/Analysis/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(`Failed to submit analysis: ${response.statusText}`);
    }
    return response.json();
  }

  async getAnalysisHistory(limit: number = 50): Promise<AnalysisRequest[]> {
    const response = await apiFetch(`${API_BASE}/api/Analysis/history?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`Failed to get analysis history: ${response.statusText}`);
    }
    return response.json();
  }

  async getAnalysisResult(requestId: string): Promise<AnalysisRequest> {
    const response = await apiFetch(`${API_BASE}/api/Analysis/${requestId}`);
    if (!response.ok) {
      throw new Error(`Failed to get analysis result: ${response.statusText}`);
    }
    return response.json();
  }

  async cancelAnalysis(requestId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/api/Analysis/${requestId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to cancel analysis: ${response.statusText}`);
    }
  }

  async getChartDataForAnalysis(symbol: string, startDate: string, endDate: string, chartSettings: any): Promise<ChartData> {
    const response = await apiFetch(`${API_BASE}/api/Analysis/chart-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ symbol, startDate, endDate, chartSettings }),
    });
    if (!response.ok) {
      throw new Error(`Failed to get chart data for analysis: ${response.statusText}`);
    }
    return response.json();
  }

  // Backtest API methods
  async runBacktest(strategy: string, symbols: string[], startDate: string, endDate: string, parameters: any): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Backtest/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ strategy, symbols, startDate, endDate, parameters }),
    });
    if (!response.ok) {
      throw new Error(`Failed to run backtest: ${response.statusText}`);
    }
    return response.json();
  }

  async getBacktestResults(backtestId: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Backtest/${backtestId}`);
    if (!response.ok) {
      throw new Error(`Failed to get backtest results: ${response.statusText}`);
    }
    return response.json();
  }

  async getBacktestHistory(limit: number = 50): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Backtest/history?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`Failed to get backtest history: ${response.statusText}`);
    }
    return response.json();
  }

  // ===== Subscription Stats =====

  async getSubscriptionStats(): Promise<SubscriptionStats> {
    const response = await apiFetch(`${API_BASE}/Screener/subscription-stats`);
    if (!response.ok) {
      throw new Error(`Failed to get subscription stats: ${response.statusText}`);
    }
    return response.json();
  }

  async subscribeTopUsStocks(
    count: number = 1000,
    sortBy: string = 'exchange',
    skipAlreadySubscribed: boolean = true
  ): Promise<BulkSubscribeResult> {
    const params = new URLSearchParams({
      count: String(count),
      sortBy,
      skipAlreadySubscribed: String(skipAlreadySubscribed),
    });
    const response = await apiFetch(`${API_BASE}/Screener/subscribe-top-us-stocks?${params}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to subscribe top US stocks: ${response.statusText}`);
    }
    return response.json();
  }

  async subscribeAllUsEquities(sortBy: string = 'exchange'): Promise<BulkSubscribeResult & { batches?: number }> {
    const params = new URLSearchParams({ sortBy });
    const response = await apiFetch(`${API_BASE}/Screener/subscribe-all-us-equities?${params}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to subscribe all US equities: ${response.statusText}`);
    }
    return response.json();
  }

  async subscribeBulk(symbols: string[]): Promise<BulkSubscribeResult> {
    const response = await apiFetch(`${API_BASE}/Screener/subscribe-bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbols })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to bulk subscribe: ${response.statusText}`);
    }
    return response.json();
  }

  async getMarketIndices(): Promise<MarketIndexSnapshot[]> {
    try {
      const response = await apiFetch(`${API_BASE}/Screener/market-indices`);
      if (!response.ok) return [];
      return response.json();
    } catch {
      return [];
    }
  }
}

export const apiService = new ApiService();

// Convenience exports for the dashboard components  
export const getChartData = (symbol: string, timeframe: string = '1M', barCount?: number) => apiService.getChartData(symbol, timeframe, barCount);
export const getChartDataById = (chartId: string, barCount?: number) => apiService.getChartDataById(chartId, barCount);
// Chart API exports
export const createChart = (symbol: string, timeframe: string, chartId: string) => apiService.createChart(symbol, timeframe, chartId);
export const getChart = (chartId: string, barCount?: number) => apiService.getChart(chartId, barCount);
export const getAllCharts = () => apiService.getAllCharts();
export const getChartsForSymbol = (symbol: string, exchange?: string) => apiService.getChartsForSymbol(symbol, exchange);
export const addIndicatorToChart = (chartId: string, indicatorRequest: any) => apiService.addIndicatorToChart(chartId, indicatorRequest);
export const removeIndicatorFromChart = (chartId: string, indicatorName: string) => apiService.removeIndicatorFromChart(chartId, indicatorName);
export const updateIndicatorParameters = (chartId: string, indicatorName: string, parameters: any) => apiService.updateIndicatorParameters(chartId, indicatorName, parameters);
export const deleteChart = (chartId: string) => apiService.deleteChart(chartId);
export const getAvailableIndicators = () => apiService.getAvailableIndicators();
export const refreshChart = (chartId: string) => apiService.refreshChart(chartId);

